import { Action } from 'redux';
import { ContractState, ContractUnloadState } from '../../states/ContractState';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractPractitionerAssociation } from '../../../models/Entities/Contracts/ContractPractitionerAssociation';
import { ContractProtocolAssociation } from '../../../models/Entities/Contracts/ContractProtocolAssociation';

/**
 * Reemplaza un contrato de la lista aplicando `mutation` sobre una copia.
 * Devuelve siempre una lista nueva y un contrato nuevo: nunca muta el store.
 */
function replaceContract(
  state: ContractState,
  contractId: number | undefined,
  mutation: (contract: Contract) => Contract
): Contract[] | undefined {
  const list = state.list;
  if (!list || contractId === undefined || contractId === null) return list;

  const index = list.findIndex((item: Contract) => item?.id === contractId);
  if (index === -1) return list;

  const updated = mutation({ ...list[index] });
  return list.map((item: Contract, i: number) => (i === index ? updated : item));
}

export default function ContractAssociationsReducer(
  state: ContractState | undefined,
  action: Action
): ContractState {
  const currentState = state ?? ContractUnloadState;
  const a: any = action;

  switch (a.type) {
    //#region Practitioner contracts (desde la ficha del profesional)

    case 'PRACTITIONER_CONTRACTS_DELETE_RECEIVE_ACTION':
      return {
        ...currentState,
        list: replaceContract(currentState, a.contractId, (contract) => ({
          ...contract,
          practitioners: (contract.practitioners || []).filter(
            (item: ContractPractitionerAssociation) => item.id !== a.practitionerContractId
          )
        }))
      };

    case 'PRACTITIONER_CONTRACTS_ADD_RECEIVE_ACTION':
      return {
        ...currentState,
        list: replaceContract(currentState, a.practitionerContract?.contractId, (contract) => ({
          ...contract,
          practitioners: [...(contract.practitioners || []), a.practitionerContract]
        }))
      };

    //#endregion

    //#region Asociar contrato a protocolo

    case 'CONTRACT_ASSOCIATE_TO_PROTOCOL_REQUEST_ACTION':
      return {
        ...currentState,
        isAssociatingProtocol: true,
        FailAssociatingProtocol: false,
        associationError: undefined
      };

    case 'CONTRACT_ASSOCIATE_TO_PROTOCOL_RECEIVE_ACTION':
      return {
        ...currentState,
        isAssociatingProtocol: false,
        FailAssociatingProtocol: false,
        associationError: undefined,
        list: replaceContract(currentState, a.contractProtocolAssociation?.contractId, (contract) => ({
          ...contract,
          // El backend admite un único protocolo por contrato: se reemplaza, no se acumula.
          protocol: [
            ...(contract.protocol || []).filter(
              (item: ContractProtocolAssociation) =>
                item.protocolId !== a.contractProtocolAssociation.protocolId
            ),
            a.contractProtocolAssociation
          ]
        }))
      };

    case 'CONTRACT_ASSOCIATE_TO_PROTOCOL_FAIL_ACTION':
      return {
        ...currentState,
        isAssociatingProtocol: false,
        FailAssociatingProtocol: true,
        associationError: a.error
      };

    //#endregion

    //#region Desasociar contrato de protocolo

    case 'CONTRACT_DISASSOCIATE_FROM_PROTOCOL_REQUEST_ACTION':
      return {
        ...currentState,
        isDisassociatingProtocol: true,
        FailDisassociatingProtocol: false,
        associationError: undefined
      };

    case 'CONTRACT_DISASSOCIATE_FROM_PROTOCOL_RECEIVE_ACTION':
      return {
        ...currentState,
        isDisassociatingProtocol: false,
        FailDisassociatingProtocol: false,
        associationError: undefined,
        list: replaceContract(currentState, a.contractId, (contract) => ({
          ...contract,
          protocol: (contract.protocol || []).filter(
            (item: ContractProtocolAssociation) => item.protocolId !== a.ProtocolId
          )
        }))
      };

    case 'CONTRACT_DISASSOCIATE_FROM_PROTOCOL_FAIL_ACTION':
      return {
        ...currentState,
        isDisassociatingProtocol: false,
        FailDisassociatingProtocol: true,
        associationError: a.error
      };

    //#endregion

    //#region Asociar contrato a profesional

    case 'CONTRACT_ASSOCIATE_TO_PRACTITIONER_REQUEST_ACTION':
      return {
        ...currentState,
        isAssociatingPractitioner: true,
        FailAssociatingPractitioner: false,
        associationError: undefined
      };

    case 'CONTRACT_ASSOCIATE_TO_PRACTITIONER_RECEIVE_ACTION':
      return {
        ...currentState,
        isAssociatingPractitioner: false,
        FailAssociatingPractitioner: false,
        associationError: undefined,
        list: replaceContract(currentState, a.contractPractitionerAssociation?.contractId, (contract) => ({
          ...contract,
          practitioners: [
            ...(contract.practitioners || []).filter(
              (item: ContractPractitionerAssociation) =>
                item.practitionerId !== a.contractPractitionerAssociation.practitionerId
            ),
            a.contractPractitionerAssociation
          ]
        }))
      };

    case 'CONTRACT_ASSOCIATE_TO_PRACTITIONER_FAIL_ACTION':
      return {
        ...currentState,
        isAssociatingPractitioner: false,
        FailAssociatingPractitioner: true,
        associationError: a.error
      };

    //#endregion

    //#region Desasociar contrato de profesional

    case 'CONTRACT_DISASSOCIATE_FROM_PRACTITIONER_REQUEST_ACTION':
      return {
        ...currentState,
        isDisassociatingPractitioner: true,
        FailDisassociatingPractitioner: false,
        associationError: undefined
      };

    case 'CONTRACT_DISASSOCIATE_FROM_PRACTITIONER_RECEIVE_ACTION':
      return {
        ...currentState,
        isDisassociatingPractitioner: false,
        FailDisassociatingPractitioner: false,
        associationError: undefined,
        list: replaceContract(currentState, a.contractId, (contract) => ({
          ...contract,
          practitioners: (contract.practitioners || []).filter(
            (item: ContractPractitionerAssociation) => item.id !== a.contractPractitionerId
          )
        }))
      };

    case 'CONTRACT_DISASSOCIATE_FROM_PRACTITIONER_FAIL_ACTION':
      return {
        ...currentState,
        isDisassociatingPractitioner: false,
        FailDisassociatingPractitioner: true,
        associationError: a.error
      };

    //#endregion

    default:
      return currentState;
  }
}
